import {
  DashboardCalendarDay,
  DashboardCalendarTournamentRef,
  DashboardTournamentCalendar,
  DashboardTournamentCategoryRef,
  DashboardEntryCategory,
  DashboardTournamentEntryContext,
  DashboardTournamentEntryListItem,
  DashboardTournamentListItem,
  DashboardTournamentOverview,
  DashboardTournamentRef,
  DashboardTournamentResultEntry,
  DashboardTournamentResultListItem,
  DashboardTournamentStats,
  DashboardTournamentContext,
} from "@/api/__generated__/schemas";
import {
  CompetitionLevel as ApiCompetitionLevel,
  Region as ApiRegion,
  TournamentTier as ApiTournamentTier,
  fromApiDate,
  RankingTournamentCategoryFragment,
  TournamentCategoryFragment,
  TournamentCategoryLicenseFragment,
  TournamentCategoryWithDisciplineFragment,
  TournamentCategoryWithLeagueFragment,
  TournamentFragment,
  TournamentRankingFragment,
  TournamentRankingsEntryFragment,
  TournamentStatsFragment,
  TournamentVenueFragment,
  TournamentWithCategoriesFragment,
  TournamentWithClubFragment,
  TournamentWithCreatedByFragment,
  TournamentWithLeagueFragment,
  TournamentWithOrganizerFragment,
  TournamentWithSportsFragment,
  TournamentWithVenuesFragment,
  fromApiDateNullable,
} from "@/graphql";
import { mapToBaseClub, mapToSport, mapToSportRef } from "@/mappers";
import {
  mapToLeague,
  mapToLeagueNamingNullable,
  mapToLeagueWithSports,
} from "@/mappers/league.mappers";
import {
  CalendarDayBucket,
  CalendarTournament,
  CompetitionLevel,
  TournamentCalendarSummary,
  TournamentStatsSummary,
  RankingTournamentCategory,
  Region,
  Tournament,
  TournamentApprovalStatus,
  TournamentRef,
  TournamentPickerOption,
  TournamentTier,
  TournamentCategory,
  TournamentCategoryLicense,
  TournamentCategoryWithDiscipline,
  TournamentCategoryWithLeague,
  TournamentListItem,
  TournamentEntry,
  TournamentEntryCategory,
  TournamentEntriesContext,
  TournamentOverview,
  TournamentPageContext,
  TournamentResult,
  TournamentResultEntry,
  TournamentRanking,
  TournamentRankingsEntry,
  TournamentStats,
  TournamentVenue,
  TournamentWithCategories,
  TournamentWithClub,
  TournamentWithCreatedBy,
  TournamentWithLeague,
  TournamentWithOrganizer,
  TournamentWithSports,
  TournamentWithVenues,
} from "@/types";

import { mapToBaseCategory, mapToCategoryGender } from "./category.mappers";
import { mapToCountryCodeNullable } from "./common.mappers";
import { mapToDiscipline } from "./discipline.mappers";
import { mapToUser } from "./user.mappers";

export const mapToTournamentCategory = (
  fragment: TournamentCategoryFragment
): TournamentCategory => ({
  id: fragment.id != null ? String(fragment.id) : "",
  name: fragment.name,
  type: fragment.type,
  gender: fragment.gender ? mapToCategoryGender(fragment.gender) : null,
  order: fragment.order ?? null,
  enableMetrics: fragment.enableMetrics ?? false,
  team: fragment.team ?? null,
  isAllowed: fragment.isAllowed ?? false,
  moveToSingles: fragment.moveToSingles ?? false,
  categoryEntries: fragment.categoryEntries ?? null,
  enableLicense: fragment.enableLicense ?? false,
  status: fragment.status ?? null,
  ageLimit: fragment.ageLimit ?? null,
  ageType: fragment.ageType ?? null,
});

/**
 * Map the slim REST `DashboardTournamentCategoryRef` into the `TournamentCategory`
 * domain shape used by filter chips / async multi-selects. The ref endpoint only
 * returns `{ id, name, sportId }`; the remaining domain fields are filled with
 * benign defaults because filter chips read only `id` + `name`.
 */
export const mapToTournamentCategoryFromRef = (
  ref: DashboardTournamentCategoryRef
): TournamentCategory => ({
  id: ref.id,
  name: ref.name ?? "",
  type: "single",
  gender: null,
  order: null,
  enableMetrics: false,
  team: null,
  isAllowed: false,
  moveToSingles: false,
  categoryEntries: null,
  enableLicense: false,
  status: null,
  ageLimit: null,
  ageType: null,
});

export const mapToTournamentEntryCategory = (
  category: DashboardEntryCategory
): TournamentEntryCategory => ({
  id: category.id,
  name: category.name,
  type: category.type,
});

export const mapToTournamentEntry = (entry: DashboardTournamentEntryListItem): TournamentEntry => ({
  id: entry.id,
  participants: entry.participants.map((participant) => ({
    id: participant.id,
    name: participant.name,
    surname: participant.surname,
    avatarFile: null,
    legacyFileUrl: participant.avatar,
    nation: participant.nation,
  })),
  team: entry.team
    ? {
        id: entry.team.id,
        title: entry.team.title,
        logoFile: null,
        legacyLogoUrl: entry.team.image,
      }
    : null,
  category: entry.category ? mapToTournamentEntryCategory(entry.category) : null,
  tournament: {
    id: entry.tournament.id,
    title: entry.tournament.title,
    logoFile: null,
    legacyLogoUrl: entry.tournament.image,
    league: entry.tournament.league
      ? {
          id: entry.tournament.league.id,
          title: entry.tournament.league.title,
          naming: mapToLeagueNamingNullable(entry.tournament.league.tourType ?? null),
        }
      : null,
  },
  registeredAt: entry.registeredAt,
});

export const mapToTournamentEntriesContext = (
  context: DashboardTournamentEntryContext
): TournamentEntriesContext => ({
  tournament: {
    id: context.tournament.id,
    title: context.tournament.title,
    logoFile: null,
    legacyLogoUrl: context.tournament.image,
    league: context.tournament.league
      ? {
          id: context.tournament.league.id,
          title: context.tournament.league.title,
          naming: mapToLeagueNamingNullable(context.tournament.league.tourType ?? null),
        }
      : null,
  },
});

export const mapToTournamentOverview = (
  overview: DashboardTournamentOverview
): TournamentOverview => ({
  id: overview.id,
  internalId: overview.internalId,
  title: overview.title,
  address: overview.address,
  city: overview.city,
  country: overview.country,
  startDate: fromApiDate(overview.startDate),
  endDate: overview.endDate ? fromApiDate(overview.endDate) : null,
  entryDeadline: overview.entryDeadline ? fromApiDate(overview.entryDeadline) : null,
  registrationStartDate: overview.registrationStartDate
    ? fromApiDate(overview.registrationStartDate)
    : null,
  status: overview.status,
  registrationStatus: overview.registrationStatus,
  logoFile: null,
  legacyLogoUrl: overview.logo,
  posterFile: null,
  legacyPosterUrl: overview.poster,
  coverPhotoFile: null,
  legacyCoverPhotoUrl: overview.coverPhoto,
  organizer: overview.organizer
    ? {
        id: overview.organizer.id,
        name: overview.organizer.name,
        surname: overview.organizer.surname,
        avatarFile: null,
        legacyFileUrl: overview.organizer.avatar,
      }
    : null,
  referee: overview.referee
    ? {
        id: overview.referee.id,
        name: overview.referee.name,
        surname: overview.referee.surname,
        avatarFile: null,
        legacyFileUrl: overview.referee.avatar,
      }
    : null,
  league: overview.league
    ? {
        id: overview.league.id,
        title: overview.league.title,
        logoFile: null,
        legacyLogoUrl: overview.league.logo,
        naming: mapToLeagueNamingNullable(overview.league.tourType ?? null),
      }
    : null,
  organization: overview.federation
    ? {
        id: overview.federation.id,
        title: overview.federation.organizationName,
        logoFile: null,
        legacyLogoUrl: overview.federation.logo,
      }
    : null,
  platform: overview.platform
    ? {
        id: overview.platform.id,
        title: overview.platform.title,
        logoFile: null,
        legacyLogoUrl: overview.platform.logo,
      }
    : null,
  sport: overview.sport ? { title: overview.sport.title, icon: overview.sport.icon } : null,
});

export const mapToTournamentPageContext = (
  context: DashboardTournamentContext
): TournamentPageContext => ({
  id: context.id,
  title: context.title,
  league: context.league
    ? {
        id: context.league.id,
        title: context.league.title,
        naming: mapToLeagueNamingNullable(context.league.tourType ?? null),
      }
    : null,
});

export const mapToTournamentResult = (
  result: DashboardTournamentResultListItem
): TournamentResult => ({
  id: result.id,
  name: result.name,
  isPublished: result.isPublished,
  submittedAt: fromApiDate(result.submittedAt),
  updatedAt: fromApiDate(result.updatedAt),
  category: result.category ? { id: result.category.id, name: result.category.name } : null,
  tournament: {
    id: result.tournament.id,
    title: result.tournament.title,
    startDate: fromApiDate(result.tournament.startDate),
    endDate: result.tournament.endDate ? fromApiDate(result.tournament.endDate) : null,
  },
});

export const mapToTournamentResultEntry = (
  result: DashboardTournamentResultEntry
): TournamentResultEntry => ({
  place: result.place,
  points: result.points,
  entry: result.entry
    ? {
        id: result.entry.id,
        participants: result.entry.participants.map((participant) => ({
          id: participant.id,
          name: participant.name,
          surname: participant.surname,
          nation: participant.nation,
          avatarFile: null,
          legacyFileUrl: participant.avatar,
        })),
        team: result.entry.team
          ? {
              id: result.entry.team.id,
              title: result.entry.team.title,
              country: result.entry.team.country,
              logoFile: null,
              legacyLogoUrl: result.entry.team.image,
            }
          : null,
      }
    : null,
});

/**
 * Inbound: a tournament from the dashboard tournament list → a row of the shared
 * tournaments picker. Venue country / city win over the deprecated denormalized
 * scalars on the tournament row.
 *
 * `categoryIds` is deliberately NOT populated: the list DTO exposes its
 * categories as the numeric `tournament_category.id`, while the picker's prune
 * check compares against the tournament-category *public* ids (`cat_…`) the
 * categories field stores. Filling it with numeric ids matched nothing, so every
 * pick was pruned as soon as the selected categories changed. Left absent, a pick
 * is "left untouched" — the same fallback edit-prefilled refs already use.
 */
export const mapToTournamentPickerOption = (
  item: DashboardTournamentListItem
): TournamentPickerOption => {
  const venue = item.venues[0] ?? null;

  return {
    id: item.id,
    title: item.title,
    coverPhoto: item.coverPhoto,
    startDate: item.startDate,
    endDate: item.endDate,
    country: venue?.country ?? item.country,
    city: venue?.city ?? item.city,
  };
};

/**
 * Map the slim REST `DashboardTournamentRef` (id, title, logo, startDate,
 * endDate) into the `TournamentRef` domain shape used by the matches-page
 * header and other places that only need identity + dates.
 */
export const mapToTournamentRef = (ref: DashboardTournamentRef): TournamentRef => ({
  id: ref.id,
  internalId: ref.internalId,
  title: ref.title,
  logo: ref.logo,
  startDate: fromApiDate(ref.startDate),
  endDate: ref.endDate ? fromApiDate(ref.endDate) : null,
});

export const mapToTournamentCategoryWithLeague = (
  fragment: TournamentCategoryWithLeagueFragment
): TournamentCategoryWithLeague => ({
  league: fragment.league
    ? {
        id: fragment.league.id,
        league: fragment.league.league
          ? {
              id: String(fragment.league.league.id),
              title: fragment.league.league.title ?? "",
            }
          : null,
      }
    : null,
});

export const mapToTournamentCategoryWithDiscipline = (
  fragment: TournamentCategoryWithDisciplineFragment
): TournamentCategoryWithDiscipline => ({
  discipline: fragment.discipline ? mapToDiscipline(fragment.discipline) : null,
});

export const mapToRankingTournamentCategory = (
  fragment: RankingTournamentCategoryFragment
): RankingTournamentCategory => ({
  id: fragment.id!,
  tournamentCategory: mapToTournamentCategory(fragment.tournamentCategory!),
  rankingCategory: fragment.rankingCategory ? mapToBaseCategory(fragment.rankingCategory) : null,
});

export const mapToTournamentRankingsEntry = (
  fragment: TournamentRankingsEntryFragment
): TournamentRankingsEntry => ({
  id: fragment.id!,
  place: fragment.place ?? null,
  points: fragment.points ?? null,
  entry: fragment.entry
    ? {
        id: fragment.entry.id,
        users:
          fragment.entry?.users?.map((user) => ({
            id: user.id!,
            user: mapToUser(user.user!),
          })) ?? [],
      }
    : null,
});

export const mapToTournamentRanking = (fragment: TournamentRankingFragment): TournamentRanking => ({
  id: fragment.id!,
  name: fragment.name ?? "",
  isPublish: fragment.isPublish ?? false,
  createdAt: fromApiDate(fragment.createdAt),
  tournament: {
    id: String(fragment.tournament!.id),
    title: fragment.tournament!.title ?? "",
    startDate: fromApiDate(fragment.tournament!.startDate),
    endDate: fromApiDate(fragment.tournament!.endDate),
  },
  tournamentCategory: {
    id: fragment.tournamentCategory!.id,
    category: mapToTournamentCategory(fragment.tournamentCategory!.category!),
  },
  rankings: fragment.rankings?.map(mapToTournamentRankingsEntry) ?? [],
});

export const mapToTournamentCategoryLicense = (
  fragment: TournamentCategoryLicenseFragment
): TournamentCategoryLicense => ({
  id: fragment.id!,
  // license: mapToLicense(fragment.license!),
});

export const mapToTournament = (fragment: TournamentFragment): Tournament => {
  return {
    id: String(fragment.id!),
    internalId: fragment.id != null ? Number(fragment.id) : null,
    title: fragment.title ?? "",
    logo: fragment.logo ?? null,
    address: fragment.address ?? null,
    city: fragment.city ?? null,
    country: fragment.country ? mapToCountryCodeNullable(fragment.country) : null,
    isActive: fragment.status === "active",
    approvalStatus: fragment.approvalStatus
      ? mapToTournamentApprovalStatus(fragment.approvalStatus)
      : null,
    createdAt: fromApiDate(fragment.createdAt),
    endDate: fromApiDate(fragment.endDate),
    startDate: fromApiDate(fragment.startDate),
    poster: fragment.poster ?? null,
    entriesCount: fragment.registrationRequestCount ?? 0,
    matchesCount: fragment.matchesCount ?? 0,
    entryDeadline: fragment.entryDeadline ? fromApiDate(fragment.entryDeadline) : null,
    withdrawalDeadline: fragment.withdrawlDeadline ? fromApiDate(fragment.withdrawlDeadline) : null,
  };
};

export const mapToTournamentWithSports = (
  fragment: TournamentWithSportsFragment
): TournamentWithSports => ({
  sports: fragment.sports?.map(mapToSport) ?? [],
});

export const mapToTournamentWithOrganizer = (
  fragment: TournamentWithOrganizerFragment
): TournamentWithOrganizer => ({
  organizer: fragment.organizer?.member
    ? {
        id: String(fragment.organizer.member.id!),
        name: fragment.organizer.member.name ?? null,
        surname: fragment.organizer.member.surname ?? null,
        avatarFile: null,
        legacyFileUrl: fragment.organizer.member.avatar ?? null,
        email: null,
        phone: null,
        contactEmail: null,
        contactPhone: null,
      }
    : null,
});

export const mapToTournamentWithCreatedBy = (
  fragment: TournamentWithCreatedByFragment
): TournamentWithCreatedBy => ({
  createdBy: fragment.createdBy
    ? {
        id: String(fragment.createdBy.id!),
        name: fragment.createdBy.name ?? null,
        surname: fragment.createdBy.surname ?? null,
        avatarFile: null,
        legacyFileUrl: fragment.createdBy.avatar ?? null,
        email: null,
        phone: null,
        contactEmail: null,
        contactPhone: null,
      }
    : null,
});

export const mapToTournamentWithClub = (
  fragment: TournamentWithClubFragment
): TournamentWithClub => {
  return {
    club: fragment.club ? mapToBaseClub(fragment.club) : null,
  };
};

export const mapToTournamentWithLeague = (
  fragment: TournamentWithLeagueFragment
): TournamentWithLeague => {
  return {
    league: fragment.league?.league
      ? {
          ...mapToLeague(fragment.league.league),
          ...mapToLeagueWithSports(fragment.league.league),
        }
      : null,
  };
};

export const mapToTournamentWithCategories = (
  fragment: TournamentWithCategoriesFragment
): TournamentWithCategories => {
  return {
    categories:
      fragment.tournamentCategory?.map((categorySetting) =>
        mapToTournamentCategory(categorySetting.category!)
      ) ?? [],
  };
};

export const mapToTournamentWithVenues = (
  fragment: TournamentWithVenuesFragment
): TournamentWithVenues => {
  return {
    venues: fragment.venues?.map(mapToTournamentVenue) ?? [],
  };
};

export const mapToTournamentVenue = (fragment: TournamentVenueFragment): TournamentVenue => {
  return {
    id: fragment.id!,
    title: fragment.title ?? "",
    city: fragment.city ?? null,
    country: fragment.country ? mapToCountryCodeNullable(fragment.country) : null,
    address: fragment.address ?? null,
  };
};

export const mapToTournamentApprovalStatus = (apiStatus: string): TournamentApprovalStatus => {
  switch (apiStatus.toLowerCase()) {
    case "pending":
      return "pending";
    case "confirmed":
      return "confirmed";
    case "declined":
      return "declined";
    default:
      throw new Error(`Unexpected tournament approval status: "${apiStatus}"`);
  }
};

export const mapToTournamentStats = (fragment: TournamentStatsFragment): TournamentStats => ({
  total: fragment.allTournaments ?? 0,
  inProgress: fragment.passedTournaments ?? 0,
  upcoming: fragment.upcomingTournaments ?? 0,
  pending: fragment.pendingTournaments ?? 0,
  declined: fragment.declinedTournaments ?? 0,
  active: fragment.activeTournaments ?? 0,
  inactive: fragment.inactiveTournaments ?? 0,
  confirmed: fragment.confirmedTournaments ?? 0,
});

const tournamentTierMapReverse: { [key in TournamentTier]: ApiTournamentTier } = {
  NOT_SET: ApiTournamentTier.NotSet,
  LOCAL: ApiTournamentTier.Local,
  REGIONAL: ApiTournamentTier.Regional,
  NATIONAL: ApiTournamentTier.National,
  INTERNATIONAL: ApiTournamentTier.International,
};

export const mapToApiTournamentTier = (tier: TournamentTier): ApiTournamentTier =>
  tournamentTierMapReverse[tier];

const competitionLevelMapReverse: { [key in CompetitionLevel]: ApiCompetitionLevel } = {
  WORLD_ADULT_CHAMPIONSHIP: ApiCompetitionLevel.WorldAdultChampionship,
  WORLD_YOUTH_CHAMPIONSHIP: ApiCompetitionLevel.WorldYouthChampionship,
  WORLD_JUNIOR_CHAMPIONSHIP: ApiCompetitionLevel.WorldJuniorChampionship,
  WORLD_CUP: ApiCompetitionLevel.WorldCup,
  WORLD_CUP_QUALIFICATION: ApiCompetitionLevel.WorldCupQualification,
  WORLD_CHAMPIONSHIP_QUALIFICATION: ApiCompetitionLevel.WorldChampionshipQualification,
  CONTINENTAL_ADULT_CHAMPIONSHIP: ApiCompetitionLevel.ContinentalAdultChampionship,
  CONTINENTAL_YOUTH_CHAMPIONSHIP: ApiCompetitionLevel.ContinentalYouthChampionship,
  CONTINENTAL_JUNIOR_CHAMPIONSHIP: ApiCompetitionLevel.ContinentalJuniorChampionship,
  CONTINENTAL_CUP: ApiCompetitionLevel.ContinentalCup,
  CONTINENTAL_CHAMPIONSHIP_QUALIFICATION: ApiCompetitionLevel.ContinentalChampionshipQualification,
  REGIONAL_LEVEL: ApiCompetitionLevel.RegionalLevel,
  NATIONAL_ADULT_CHAMPIONSHIP: ApiCompetitionLevel.NationalAdultChampionship,
  NATIONAL_YOUTH_CHAMPIONSHIP: ApiCompetitionLevel.NationalYouthChampionship,
  NATIONAL_JUNIOR_CHAMPIONSHIP: ApiCompetitionLevel.NationalJuniorChampionship,
  NATIONAL_UNIVERSITY_CHAMPIONSHIP: ApiCompetitionLevel.NationalUniversityChampionship,
  NATIONAL_STUDENT_CHAMPIONSHIP: ApiCompetitionLevel.NationalStudentChampionship,
  NATIONAL_UNIVERSIADE: ApiCompetitionLevel.NationalUniversiade,
  NATIONAL_OLYMPIAD: ApiCompetitionLevel.NationalOlympiad,
  NATIONAL_CUP: ApiCompetitionLevel.NationalCup,
  OLYMPIC_GAMES_QUALIFICATION: ApiCompetitionLevel.OlympicGamesQualification,
  INTERNATIONAL_COMPETITION: ApiCompetitionLevel.InternationalCompetition,
  SENIOR_VETERAN_COMPETITION: ApiCompetitionLevel.SeniorVeteranCompetition,
  AMATEUR_SPORT_COMPETITION: ApiCompetitionLevel.AmateurSportCompetition,
  OTHER_COUNTRY_LEVEL: ApiCompetitionLevel.OtherCountryLevel,
  OTHER: ApiCompetitionLevel.Other,
};

export const mapToApiCompetitionLevel = (level: CompetitionLevel): ApiCompetitionLevel =>
  competitionLevelMapReverse[level];

const regionMapReverse: { [key in Region]: ApiRegion } = {
  LV_ADAZI_MUNICIPALITY: ApiRegion.LvAdaziMunicipality,
  LV_AIZKRAUKLE_MUNICIPALITY: ApiRegion.LvAizkraukleMunicipality,
  LV_ALUKSNE_MUNICIPALITY: ApiRegion.LvAluksneMunicipality,
  LV_AUGSDAUGAVA_MUNICIPALITY: ApiRegion.LvAugsdaugavaMunicipality,
  LV_BALVI_MUNICIPALITY: ApiRegion.LvBalviMunicipality,
  LV_BAUSKA_MUNICIPALITY: ApiRegion.LvBauskaMunicipality,
  LV_CESIS_MUNICIPALITY: ApiRegion.LvCesisMunicipality,
  LV_DAUGAVPILS_CITY: ApiRegion.LvDaugavpilsCity,
  LV_DIENVIDKURZEME_MUNICIPALITY: ApiRegion.LvDienvidkurzemeMunicipality,
  LV_DOBELE_MUNICIPALITY: ApiRegion.LvDobeleMunicipality,
  LV_GULBENE_MUNICIPALITY: ApiRegion.LvGulbeneMunicipality,
  LV_JEKABPILS_CITY: ApiRegion.LvJekabpilsCity,
  LV_JELGAVA_CITY: ApiRegion.LvJelgavaCity,
  LV_JELGAVA_MUNICIPALITY: ApiRegion.LvJelgavaMunicipality,
  LV_JURMALA_CITY: ApiRegion.LvJurmalaCity,
  LV_KEKAVA_MUNICIPALITY: ApiRegion.LvKekavaMunicipality,
  LV_KRASLAVA_MUNICIPALITY: ApiRegion.LvKraslavaMunicipality,
  LV_KULDIGA_MUNICIPALITY: ApiRegion.LvKuldigaMunicipality,
  LV_LIEPAJA_CITY: ApiRegion.LvLiepajaCity,
  LV_LIMBAZI_MUNICIPALITY: ApiRegion.LvLimbaziMunicipality,
  LV_LIVANI_MUNICIPALITY: ApiRegion.LvLivaniMunicipality,
  LV_LUDZA_MUNICIPALITY: ApiRegion.LvLudzaMunicipality,
  LV_MADONA_MUNICIPALITY: ApiRegion.LvMadonaMunicipality,
  LV_MARUPE_MUNICIPALITY: ApiRegion.LvMarupeMunicipality,
  LV_OGRE_MUNICIPALITY: ApiRegion.LvOgreMunicipality,
  LV_OLAINE_MUNICIPALITY: ApiRegion.LvOlaineMunicipality,
  LV_PREILI_MUNICIPALITY: ApiRegion.LvPreiliMunicipality,
  LV_REZEKNE_CITY: ApiRegion.LvRezekneCity,
  LV_REZEKNE_MUNICIPALITY: ApiRegion.LvRezekneMunicipality,
  LV_RIGA_CITY: ApiRegion.LvRigaCity,
  LV_ROPAZI_MUNICIPALITY: ApiRegion.LvRopaziMunicipality,
  LV_SALASPILS_MUNICIPALITY: ApiRegion.LvSalaspilsMunicipality,
  LV_SALDUS_MUNICIPALITY: ApiRegion.LvSaldusMunicipality,
  LV_SAULKRASTI_MUNICIPALITY: ApiRegion.LvSaulkrastiMunicipality,
  LV_SIGULDA_MUNICIPALITY: ApiRegion.LvSiguldaMunicipality,
  LV_SMILTENE_MUNICIPALITY: ApiRegion.LvSmilteneMunicipality,
  LV_TALSI_MUNICIPALITY: ApiRegion.LvTalsiMunicipality,
  LV_TUKUMS_MUNICIPALITY: ApiRegion.LvTukumsMunicipality,
  LV_VALKA_MUNICIPALITY: ApiRegion.LvValkaMunicipality,
  LV_VALMIERA_CITY: ApiRegion.LvValmieraCity,
  LV_VENTSPILS_CITY: ApiRegion.LvVentspilsCity,
  LV_VENTSPILS_MUNICIPALITY: ApiRegion.LvVentspilsMunicipality,
};

export const mapToApiRegion = (region: Region): ApiRegion => regionMapReverse[region];

export const mapToTournamentListItem = (item: DashboardTournamentListItem): TournamentListItem => ({
  id: item.id,
  internalId: item.internalId,
  title: item.title ?? "",
  logo: item.logo,
  address: null,
  city: item.city,
  country: item.country ? mapToCountryCodeNullable(item.country) : null,
  isActive: true,
  approvalStatus: item.approvalStatus ?? null,
  createdAt: fromApiDate(item.createdAt),
  startDate: fromApiDate(item.startDate),
  endDate: item.endDate ? fromApiDate(item.endDate) : null,
  poster: item.coverPhoto,
  entriesCount: item.entriesCount,
  matchesCount: item.matchesCount,
  entryDeadline: null,
  withdrawalDeadline: null,
  sports: item.sports.map((s) => ({ id: s.id, title: s.title ?? "", icon: s.icon })),
  organizer: item.organizer
    ? {
        id: item.organizer.id,
        name: item.organizer.name,
        surname: item.organizer.surname,
        avatarFile: null,
        legacyFileUrl: item.organizer.avatar,
        email: item.organizer.email,
        phone: item.organizer.phone,
        contactEmail: item.organizer.contactEmail,
        contactPhone: item.organizer.contactPhone,
      }
    : null,
  createdBy: item.createdBy
    ? {
        id: item.createdBy.id,
        name: item.createdBy.name,
        surname: item.createdBy.surname,
        avatarFile: null,
        legacyFileUrl: item.createdBy.avatar,
        email: item.createdBy.email,
        phone: item.createdBy.phone,
        contactEmail: null,
        contactPhone: null,
      }
    : null,
  league: item.league
    ? {
        id: item.league.id,
        title: item.league.title ?? "",
        type: "local",
        status: "active",
        email: null,
        website: null,
        logo: item.league.logo,
        bannerCoverUrl: null,
        regulationsUrl: null,
        country: null,
        sport: item.sports[0]
          ? { id: item.sports[0].id, title: item.sports[0].title ?? "", icon: item.sports[0].icon }
          : null,
      }
    : null,
  venues: item.venues.map((v) => ({
    id: v.id,
    title: v.title ?? "",
    address: v.address,
    city: v.city,
    country: v.country ? mapToCountryCodeNullable(v.country) : null,
  })),
  categories: item.categories.map((c) => ({
    id: String(c.id),
    name: c.title ?? "",
    type: "single",
    gender: null,
    order: null,
    enableMetrics: false,
    team: null,
    isAllowed: true,
    moveToSingles: false,
    categoryEntries: null,
    enableLicense: false,
    status: null,
    ageLimit: null,
    ageType: null,
  })),
  scopes: [
    {
      platform: item.platform
        ? { id: item.platform.id, title: item.platform.title ?? "", logo: item.platform.logo }
        : null,
      organization: item.federation
        ? {
            id: item.federation.id,
            name: item.federation.organizationName ?? "",
            abbreviation: item.federation.organizationAbbreviation,
            logo: item.federation.logo,
          }
        : null,
    },
  ],
});

export const mapToTournamentStatsSummary = (
  stats: DashboardTournamentStats
): TournamentStatsSummary => ({
  total: stats.total,
  active: stats.inProgress,
  upcoming: stats.upcoming,
  inProgressUpcoming: stats.inProgress + stats.upcoming,
  completed: stats.completed,
  confirmed: stats.approved,
  pending: stats.pending,
  declined: stats.declined,
});

export const mapToCalendarTournament = (
  ref: DashboardCalendarTournamentRef
): CalendarTournament | null => {
  const startDate = fromApiDateNullable(ref.startDate);
  if (!startDate) return null;
  return {
    id: ref.id,
    internalId: ref.internalId,
    title: ref.title ?? "",
    startDate,
    endDate: ref.endDate ? fromApiDateNullable(ref.endDate) : null,
    country: ref.country ?? null,
    city: ref.city ?? null,
    matchCount: ref.matchCount,
    sport: ref.sport ? mapToSportRef(ref.sport) : null,
  };
};

export const mapToCalendarDayBucket = (day: DashboardCalendarDay): CalendarDayBucket | null => {
  const date = fromApiDateNullable(day.date);
  if (!date) return null;
  return {
    date,
    tournamentCount: day.count,
    tournaments: day.tournaments
      .map(mapToCalendarTournament)
      .filter((t): t is CalendarTournament => t !== null),
  };
};

export const mapToTournamentCalendarSummary = (
  payload: DashboardTournamentCalendar
): TournamentCalendarSummary => ({
  days: payload.days.map(mapToCalendarDayBucket).filter((d): d is CalendarDayBucket => d !== null),
  nearestPastDate: payload.nearestPastDate ?? null,
  nearestFutureDate: payload.nearestFutureDate ?? null,
});
